import { AlertTriangle, CheckCircle2, Loader2, Plug, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  type ExternalIdentityDto,
  type RosterProvider,
  useLinkExternalIdentity,
  useRosterIntegrations,
  useUnlinkExternalIdentity,
  useUserExternalIdentities,
} from '../api/useRosterIntegrations.js';
import { cn } from '../lib/cn.js';

// Per-worker external identity editor. Shown as a subsection inside the Edit worker
// modal so admins can link a worker to their AlayaCare / ShiftCare employee id without
// bouncing to a separate screen.

export function ExternalIdentitiesSection({ userId }: { userId: string }) {
  const identities = useUserExternalIdentities(userId);
  const integrations = useRosterIntegrations();

  // Only offer providers the org has actually configured — no point typing an AlayaCare
  // employee id for a tenant that hasn't connected AlayaCare.
  const activeProviders = useMemo(
    () =>
      (integrations.data?.integrations ?? [])
        .filter((i) => i.active)
        .map((i) => i.provider as RosterProvider),
    [integrations.data],
  );

  const linkedByProvider = useMemo(() => {
    const map = new Map<string, ExternalIdentityDto>();
    for (const i of identities.data?.identities ?? []) {
      map.set(i.provider, i);
    }
    return map;
  }, [identities.data]);

  if (activeProviders.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-line bg-surface-3 px-3 py-2 text-[11px] text-ink-faint">
        No roster integrations active.{' '}
        <a href="/#/settings" className="font-medium text-brand-purple hover:underline">
          Configure one in Settings
        </a>{' '}
        to link this worker to an external scheduling system.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {activeProviders.map((provider) => (
        <ProviderRow
          key={provider}
          userId={userId}
          provider={provider}
          linked={linkedByProvider.get(provider) ?? null}
        />
      ))}
    </div>
  );
}

function ProviderRow({
  userId,
  provider,
  linked,
}: {
  userId: string;
  provider: RosterProvider;
  linked: ExternalIdentityDto | null;
}) {
  const [value, setValue] = useState(linked?.external_user_id ?? '');
  const link = useLinkExternalIdentity();
  const unlink = useUnlinkExternalIdentity();

  const hasChange = value.trim() !== (linked?.external_user_id ?? '');

  async function onLink() {
    if (!value.trim()) return;
    await link.mutateAsync({ userId, provider, external_user_id: value.trim() });
  }

  async function onUnlink() {
    if (!linked) return;
    if (!window.confirm(`Unlink this worker from ${provider}?`)) return;
    await unlink.mutateAsync({ userId, provider });
    setValue('');
  }

  // AlayaCare's resolver caches the provider-native numeric id under this key so the
  // sync filter uses an id the visits endpoint actually accepts. Showing it here lets
  // the admin confirm the ac_id → id lookup succeeded before kicking off a sync.
  const resolvedAlayaId =
    typeof linked?.config?.alayacare_employee_id === 'number'
      ? (linked.config.alayacare_employee_id as number)
      : null;
  const resolveError = linked?.resolve_error ?? null;
  const resolvedName = linked?.resolved_display_name ?? null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 text-xs font-medium capitalize text-ink-dim w-28 shrink-0">
          <Plug className={cn('h-3.5 w-3.5', linked ? 'text-emerald-600' : 'text-ink-mute')} />
          {provider}
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={`${provider} employee id`}
          maxLength={120}
          className="flex-1 rounded-md border border-line-strong px-2.5 py-1 font-mono text-xs focus:border-brand-purple focus:outline-none"
        />
        {hasChange && (
          <button
            type="button"
            onClick={onLink}
            disabled={!value.trim() || link.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-brand-purple px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
          >
            {link.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            {linked ? 'Update' : 'Link'}
          </button>
        )}
        {linked && !hasChange && (
          <button
            type="button"
            onClick={onUnlink}
            disabled={unlink.isPending}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-40"
          >
            <Trash2 className="h-3 w-3" />
            Unlink
          </button>
        )}
      </div>
      {linked &&
        !hasChange &&
        (resolveError ? (
          <div className="ml-[7.25rem] flex items-start gap-1 text-[11px] text-amber-700">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              Could not resolve against {provider}:{' '}
              <span className="font-medium">{resolveError}</span>. Sync will still try with the raw
              id, but may return nothing.
            </span>
          </div>
        ) : resolvedAlayaId || resolvedName ? (
          <div className="ml-[7.25rem] text-[11px] text-emerald-700">
            Resolved{resolvedName ? ` · ${resolvedName}` : ''}
            {resolvedAlayaId ? ` · internal id ${resolvedAlayaId}` : ''}
          </div>
        ) : null)}
    </div>
  );
}
